import { DateTime } from "luxon";
import { type FC, type ReactNode, useCallback, useMemo } from "react";
import { type ResolvedActualDay, resolveActualDayShifts } from "../../components/StaffTable/utils";
import { useFutureLookingCostlyStaff } from "../../hooks/useFutureLookingCostlyStaff";
import { CALL_INS_PAGE_SIZE, useGetLimitedCallOffs } from "../../hooks/useGetLimitedCallOffs";
import { useLastNameFirst } from "../../hooks/useNameFormat";
import { useNurseCosts } from "../../hooks/useNurseCosts";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { calculateTimeRange, getShiftDisplayDate, getVisibleWeekOffset } from "../../utils/dateUtils";
import {
	AbsenceType,
	type CalledOffShift,
	type Day,
	type DayItem,
	NotificationType,
	type NurseInShift,
	type PunchSummary,
	type ShiftRequest,
	type StatsCalculationParams,
} from "../../utils/models";
import { computePunchSummary } from "../../utils/punchSummary";
import { calculateDayStats } from "../../utils/statsCalculations";
import { FEATURE_FLAGS } from "../FeatureFlagsProvider";
import { useHospitalData } from "../HospitalDataProvider";
import { useRequestsContext } from "../RequestsContext";
import { useWeekIndex } from "../WeekIndexProvider";
import { usePtoByDayISO } from "./usePtoByDayISO";
import { WeekPunchSummaryContext } from "./WeekPunchSummaryContext";
import { WeekViewContext } from "./WeekViewContext";

type BaseDayItem = Omit<DayItem, "pto">;

const RECENT_CALL_INS_LOOKBACK_DAYS = 30;

// Messages on `CalledOffShift` that are management-initiated, NOT a nurse calling in.
// Anything else (including custom INH-3258 reasons like "Sick", "Family Obligations") is a call-in.
const NON_CALL_IN_MESSAGES = new Set<string>([
	AbsenceType.NO_SHOW,
	AbsenceType.OVERSTAFFED,
	AbsenceType.LOW_CENSUS,
	AbsenceType.CANCEL,
	AbsenceType.SUSPENSION,
]);

const isCallInMessage = (message: string | undefined | null): boolean => !!message && !NON_CALL_IN_MESSAGES.has(message);

export const WeekViewProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const { weekIndex, viewStartDay } = useWeekIndex();
	const { hospitalData, chosenDepartment, isHospital, federalHolidaysUSASet, departmentNurseTypes, wageData, getNurseById, scheduledShiftsById, timezone } =
		useHospitalData();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const lastNameFirst = useLastNameFirst();
	const { staffRequests } = useRequestsContext();
	const isDynamicWeekStartEnabled = useFeatureFlag(FEATURE_FLAGS.StaffingDynamicWeekStart);

	const { baseWeekViewData, baseVisibleWeekViewData } = useMemo<{
		baseWeekViewData: BaseDayItem[];
		baseVisibleWeekViewData: BaseDayItem[];
	}>(() => {
		const empty = { baseWeekViewData: [], baseVisibleWeekViewData: [] };
		if (!hospitalData.nurses.length || !hospitalData.weeks?.[weekIndex]) return empty;

		const requestsMap = staffRequests.reduce((map, request) => {
			const requestDate = getShiftDisplayDate(request.message.shift_start, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes);

			const patchedRequestDate = requestDate.startOf("day");

			const dateKey: number = patchedRequestDate.toSeconds();
			map.set(dateKey, [...(map.get(dateKey) || []), request]);

			if (request.request_type === NotificationType.Swap && request.request_target_shift) {
				const targetShift = scheduledShiftsById.get(request.request_target_shift);
				if (targetShift) {
					const targetDate = getShiftDisplayDate(targetShift.startTime, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes);
					const targetDateKey: number = targetDate.startOf("day").toSeconds();
					if (targetDateKey !== dateKey) {
						map.set(targetDateKey, [...(map.get(targetDateKey) || []), request]);
					}
				}
			}

			return map;
		}, new Map<number, ShiftRequest[]>());

		// Build a BaseDayItem for a specific (data-week, dayIndex). Returns null when the source week
		// is missing or has no day at that index — the caller filters nulls.
		const buildBaseDayItem = (sourceWeekIndex: number, sourceDayIndex: number): BaseDayItem | null => {
			const sourceWeek = hospitalData.weeks[sourceWeekIndex];
			if (!sourceWeek) return null;

			const days: Day[] = sourceWeek.weeklyShifts.map((shift) => shift.days[sourceDayIndex]).filter((d): d is Day => Boolean(d));
			if (!days.length) return null;

			const date: DateTime = days[0].date;
			const shifts: NurseInShift[] = days.flatMap((day) => day.nurses);
			const requests: ShiftRequest[] = requestsMap.get(date.toSeconds()) ?? [];

			// Stats must operate on the source data-week, not the visible window — OT, wages,
			// and totals are aligned to department.week_start_day and don't follow viewStartDay.
			const params: StatsCalculationParams = {
				nurses: hospitalData.nurses,
				department: chosenDepartment,
				timezone: chosenDepartment.timezone,
				rolesToDisplay: departmentNurseTypes,
				hospitalData,
				federalHolidaysUSASet,
				weekIndex: sourceWeekIndex,
				weeklyShifts: sourceWeek.weeklyShifts,
				dayIndex: sourceDayIndex,
				wageData,
				jobManagementEnabled,
			};

			return {
				date,
				shifts,
				shiftsActual: days.flatMap((day) => day.nursesActual || []),
				requests,
				dayStats: calculateDayStats(params, !isHospital, chosenDepartment.timezone),
				dayStatsActual: calculateDayStats(params, !isHospital, chosenDepartment.timezone, true),
				...calculateTimeRange(shifts, requests),
			};
		};

		const baseWeekViewData = Array.from({ length: 7 }, (_, i) => buildBaseDayItem(weekIndex, i)).filter((item): item is BaseDayItem => item !== null);

		// Flag off → offset 0 → branch below reuses baseWeekViewData by identity, skipping rotation work.
		const offset = isDynamicWeekStartEnabled ? getVisibleWeekOffset(viewStartDay) : 0;
		const baseVisibleWeekViewData =
			offset === 0
				? baseWeekViewData
				: Array.from({ length: 7 }, (_, visiblePosition) => {
						const totalIndex = visiblePosition + offset;
						return totalIndex < 7 ? buildBaseDayItem(weekIndex, totalIndex) : buildBaseDayItem(weekIndex + 1, totalIndex - 7);
					}).filter((item): item is BaseDayItem => item !== null);

		return { baseWeekViewData, baseVisibleWeekViewData };
	}, [
		hospitalData,
		staffRequests,
		chosenDepartment,
		isHospital,
		departmentNurseTypes,
		federalHolidaysUSASet,
		weekIndex,
		viewStartDay,
		isDynamicWeekStartEnabled,
		wageData,
		jobManagementEnabled,
		scheduledShiftsById,
	]);

	// Spans data-week + spillover days so aux maps (OT, PTO, call-offs, punches) cover both.
	// Identity-equal to baseWeekViewData when offset=0 — default users skip the union work.
	const baseDayItemsForAuxData = useMemo<BaseDayItem[]>(() => {
		if (baseVisibleWeekViewData === baseWeekViewData) return baseWeekViewData;
		const seen = new Set<string>();
		const out: BaseDayItem[] = [];
		for (const source of [baseWeekViewData, baseVisibleWeekViewData]) {
			for (const di of source) {
				const iso = di.date.toISODate();
				if (iso && !seen.has(iso)) {
					seen.add(iso);
					out.push(di);
				}
			}
		}
		return out;
	}, [baseWeekViewData, baseVisibleWeekViewData]);

	const futureLookingWeeklyOvertimeEnabled = useFeatureFlag(FEATURE_FLAGS.FutureLookingWeeklyOvertime);
	const { costlyShiftsByDay, shiftHoursById, actualShiftIds } = useFutureLookingCostlyStaff(baseDayItemsForAuxData, futureLookingWeeklyOvertimeEnabled);

	const weekStart = baseDayItemsForAuxData[0]?.date.startOf("day");
	const weekEnd = baseDayItemsForAuxData[baseDayItemsForAuxData.length - 1]?.date.endOf("day");
	const today = useMemo(() => DateTime.now().startOf("day"), []);

	const fromDate = useMemo<DateTime | undefined>(() => {
		const lookbackStart = today.minus({ days: RECENT_CALL_INS_LOOKBACK_DAYS });
		return weekStart && weekStart < lookbackStart ? weekStart : lookbackStart;
	}, [today, weekStart]);
	const toDate = useMemo<DateTime | undefined>(() => (weekEnd && weekEnd > today ? weekEnd : today), [today, weekEnd]);
	const { combinedData: callOffsResponse } = useGetLimitedCallOffs({
		page: 1,
		pageSize: CALL_INS_PAGE_SIZE,
		fromDate,
		toDate,
	});

	const callOffsByDayISO = useMemo<Map<string, CalledOffShift[]>>(() => {
		const map = new Map<string, CalledOffShift[]>();
		const results = callOffsResponse?.results ?? [];
		for (const callOff of results) {
			if (!isCallInMessage(callOff.message)) continue;
			const dayKey = getShiftDisplayDate(callOff.startTime, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes)
				.startOf("day")
				.toISODate();
			if (!dayKey) continue;
			const existing = map.get(dayKey);
			if (existing) existing.push(callOff);
			else map.set(dayKey, [callOff]);
		}
		return map;
	}, [callOffsResponse, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes]);

	const recentCallInsCounts = useMemo<Record<number, number>>(() => {
		const cutoff = today.minus({ days: RECENT_CALL_INS_LOOKBACK_DAYS });
		const counts: Record<number, number> = {};
		for (const callOff of callOffsResponse?.results ?? []) {
			if (!isCallInMessage(callOff.message)) continue;
			if (callOff.startTime < cutoff || callOff.startTime > today) continue;
			counts[callOff.nurseId] = (counts[callOff.nurseId] ?? 0) + 1;
		}
		return counts;
	}, [callOffsResponse, today]);

	const ptoByDayISO = usePtoByDayISO(
		baseDayItemsForAuxData.map((day) => day.date),
		hospitalData.nurses,
		chosenDepartment.dayStart,
		timezone,
	);

	const ptoCostsRangeStart = useMemo(() => weekStart?.toISODate() ?? undefined, [weekStart]);
	const ptoCostsRangeEnd = useMemo(() => weekEnd?.toISODate() ?? undefined, [weekEnd]);
	const { data: ptoCosts } = useNurseCosts({ startDate: ptoCostsRangeStart, endDate: ptoCostsRangeEnd });

	const ptoCostByDayByNurse = useMemo<Map<string, Map<number, number>>>(() => {
		const map = new Map<string, Map<number, number>>();
		if (!ptoCosts) return map;
		for (const item of ptoCosts) {
			const iso = item.date.toISODate();
			if (!iso) continue;
			let inner = map.get(iso);
			if (!inner) {
				inner = new Map<number, number>();
				map.set(iso, inner);
			}
			inner.set(item.nurseId, (inner.get(item.nurseId) ?? 0) + item.cost);
		}
		return map;
	}, [ptoCosts]);

	const getPtoCost = useCallback(
		(date: DateTime, nurseId: number): number | undefined => {
			const iso = date.toISODate();
			if (!iso) return undefined;
			return ptoCostByDayByNurse.get(iso)?.get(nurseId);
		},
		[ptoCostByDayByNurse],
	);

	const punchSummaryReady = callOffsResponse !== undefined;

	// Keep `weekViewData`'s deps synchronous-only so its identity stays stable
	// when async queries resolve post-paint — protects the staffing grid's memo barriers.
	const weekViewData = useMemo<DayItem[]>(() => {
		return baseWeekViewData.map((day) => {
			const iso = day.date.toISODate() ?? "";
			const pto = ptoByDayISO.get(iso) ?? [];
			return { ...day, pto };
		});
	}, [baseWeekViewData, ptoByDayISO]);

	const visibleWeekViewData = useMemo<DayItem[]>(() => {
		// Reuse weekViewData when offset is 0 so identity stays stable for downstream memos.
		if (baseVisibleWeekViewData === baseWeekViewData) return weekViewData;
		return baseVisibleWeekViewData.map((day) => {
			const iso = day.date.toISODate() ?? "";
			const pto = ptoByDayISO.get(iso) ?? [];
			return { ...day, pto };
		});
	}, [baseVisibleWeekViewData, baseWeekViewData, weekViewData, ptoByDayISO]);

	// Outside the `punchSummaryReady` gate below because resolving shifts needs no async data. The
	// grouped sections are all punch-summary derived and so stay empty until that gate opens, but the
	// timeline, the row counts and `nurseIdsToIgnore` read this map directly and can populate sooner.
	const resolvedActualByDate = useMemo<Map<string, ResolvedActualDay>>(() => {
		const map = new Map<string, ResolvedActualDay>();
		for (const day of baseDayItemsForAuxData) {
			const iso = day.date.toISODate();
			if (!iso) continue;
			map.set(
				iso,
				resolveActualDayShifts({
					scheduledShifts: day.shifts,
					actualShifts: day.shiftsActual,
					getNurseById,
					ptoNurseIds: new Set((ptoByDayISO.get(iso) ?? []).map((preference) => preference.nurse_id)),
				}),
			);
		}
		return map;
	}, [baseDayItemsForAuxData, getNurseById, ptoByDayISO]);

	const scheduledShiftsByNurseIdForEligibility = useMemo<Map<number, NurseInShift[]>>(() => {
		const byNurseId = new Map<number, NurseInShift[]>();
		for (const shift of scheduledShiftsById.values()) {
			const existing = byNurseId.get(shift.nurseId);
			if (existing) existing.push(shift);
			else byNurseId.set(shift.nurseId, [shift]);
		}
		return byNurseId;
	}, [scheduledShiftsById]);

	const punchSummariesByDate = useMemo<Map<string, PunchSummary>>(() => {
		const map = new Map<string, PunchSummary>();
		if (!punchSummaryReady) return map;
		for (const day of baseDayItemsForAuxData) {
			const iso = day.date.toISODate();
			const resolvedActual = iso && resolvedActualByDate.get(iso);
			if (!iso || !resolvedActual) continue;
			map.set(
				iso,
				computePunchSummary({
					shifts: day.shifts,
					scheduledShiftsByNurseIdForEligibility,
					resolvedActual,
					getNurseById,
					department: chosenDepartment,
					pto: ptoByDayISO.get(iso) ?? [],
					jobManagementEnabled,
					lastNameFirst,
					costlyShiftsForDay: costlyShiftsByDay.get(iso) ?? [],
					shiftHoursById,
					callOffsForDay: callOffsByDayISO.get(iso) ?? [],
				}),
			);
		}
		return map;
	}, [
		baseDayItemsForAuxData,
		scheduledShiftsByNurseIdForEligibility,
		getNurseById,
		chosenDepartment,
		ptoByDayISO,
		jobManagementEnabled,
		lastNameFirst,
		costlyShiftsByDay,
		shiftHoursById,
		callOffsByDayISO,
		punchSummaryReady,
		resolvedActualByDate,
	]);

	const contextValue = useMemo(
		() => ({ weekViewData, visibleWeekViewData, costlyShiftsByDay, shiftHoursById, actualShiftIds, recentCallInsCounts, getPtoCost }),
		[weekViewData, visibleWeekViewData, costlyShiftsByDay, shiftHoursById, actualShiftIds, recentCallInsCounts, getPtoCost],
	);

	const punchSummaryContextValue = useMemo(() => ({ punchSummariesByDate, resolvedActualByDate }), [punchSummariesByDate, resolvedActualByDate]);

	return (
		<WeekViewContext.Provider value={contextValue}>
			<WeekPunchSummaryContext.Provider value={punchSummaryContextValue}>{children}</WeekPunchSummaryContext.Provider>
		</WeekViewContext.Provider>
	);
};
